// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/GatewaySettingsPage.tsx
//
// Phase 3.2 Task 34 — Admin Gateway Settings page (9 plugins).
// Phase 8.2 Task 08 — Payment Gateways platform surface (extended).
//
// Per CONTEXT.md (tiered plugin model — 9 plugins, hot-swap for
// Payoneer/LianLian/Airwallex, encrypted credentials, per-tier UI affordances).
// Covers goal G-GW-15.
//
// Per CONTEXT.md (extended in Phase 8.2):
//   - Masked credentials by default (`****XXXX`) — Reveal modal requires
//     password confirm + auto re-mask after 30s + audit-log on every reveal.
//   - Fee config: flat / percent / combined model (vendor bears fee).
//   - Pause/Resume gateway: manual flow, flips merchant-portal Maintenance
//     badge. No auto-switch on failure — queue pause UI banner only.
//   - 60s health-check poll surfaces last health status badge.
//
// vg-binding: CONTEXT:decisions=P4.1.D-21
//   - (Payment Methods MVP — Amendment #8 2026-05-04) — 2-level
//     design: this page is the PLATFORM-GLOBAL surface managing 9 specific
//     gateway plugins (Tier 1: paypal/stripe/payoneer/lianlian; Tier 2:
//     sunrate/pingpong/worldfirst/airwallex/bank). The PER-MERCHANT
//     surface is BillingConfigForm in MerchantBillingPage.tsx — there
//     merchants pick the broad CATEGORY (BANK_TRANSFER vs GATEWAY) and
//     the platform-active subset filters automatically.
//     Original spec called for 9 checkboxes per merchant; A3 manual smoke
//     2026-05-04 surfaced this 2-level design as the actual implementation
//     (cleaner UX, central plugin management). Amendment #8 documents
//     this realignment retroactively — no code change.
//
// Endpoints (Phase 3.2, per API-CONTRACTS.md L2583-2668):
//   GET  /api/v1/admin/gateway-settings/:gateway
//   PUT  /api/v1/admin/gateway-settings/:gateway   (enabled / tier_mode / credentials)
//   POST /api/v1/admin/gateway-settings/:gateway/test-connection
//   POST /api/v1/admin/gateway-settings/:gateway/regenerate-webhook
//
// Endpoints (Phase 8.2, per .vg/phases/08.2-.../API-CONTRACTS.md L664-985):
//   GET   /api/v1/admin/payment-gateways                  — list + masked_key + fee + health
//   PATCH /api/v1/admin/payment-gateways/:id              — status / fee / auto_approve + reason
//   POST  /api/v1/admin/payment-gateways/:id/reveal       — password-gated, 30s re-mask, audit
//   POST  /api/v1/admin/payment-gateways/:id/pause        — manual queue pause + audit
//   POST  /api/v1/admin/payment-gateways/:id/resume       — manual resume + audit
//
// Design: admin-gateway-settings.png — pill tab row across the 9 gateways,
// active gateway card with health badge, Enabled toggle, Tier mode select,
// Credentials grid, Webhook URL + Regenerate (Tier 1) OR IMAP pattern preview
// (Tier 2), Test connection (Tier 1) + Save buttons.
//
// vg-binding: PLAN:task-08 (Phase 8.2)
// vg-binding: P8.D-85
// vg-binding: P8.D-453
// vg-binding: P8.D-454
// vg-binding: BR-18
//
// Phase 9.2 i18n hardening (task-05):
// vg-binding: P9.2.D-01
// vg-binding: P9.2.D-02
// vg-binding: P9.2.D-04
// vg-binding: P9.2.D-11
// vg-binding: P9.2.D-12
// vg-binding: PLAN:task-05 (Phase 9.2)

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useToast,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { apiClient as openapiApiClient } from '@printway/shared/api';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import { ApiError, apiClient as p8ApiClient } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { GatewayRevealModal } from '../../features/billing/GatewayRevealModal.js';
import {
  GatewayFeeForm,
  type GatewayFeeConfig,
} from '../../features/billing/GatewayFeeForm.js';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from './billing-shared.js';

//  9-plugin registry (4 Tier 1 + 4 Tier 2 + bank).
const TIER1_GATEWAYS = ['paypal', 'stripe', 'payoneer', 'lianlian'] as const;
const TIER2_GATEWAYS = ['sunrate', 'pingpong', 'worldfirst', 'airwallex', 'bank'] as const;
const ALL_GATEWAYS = [...TIER1_GATEWAYS, ...TIER2_GATEWAYS] as const;
type Gateway = (typeof ALL_GATEWAYS)[number];

// Hot-swap allowed for Payoneer / LianLian / Airwallex (F3.6).
const HOT_SWAP_GATEWAYS: ReadonlySet<Gateway> = new Set(['payoneer', 'lianlian', 'airwallex']);

// Display labels for the pill tabs.
const GATEWAY_LABEL: Record<Gateway, string> = {
  paypal: 'PayPal',
  stripe: 'Stripe',
  payoneer: 'Payoneer',
  lianlian: 'LianLian',
  sunrate: 'Sunrate',
  pingpong: 'Pingpong',
  worldfirst: 'WorldFirst',
  airwallex: 'Airwallex',
  bank: 'Bank IMAP',
};

// Per-gateway credential field schema (display-only — values masked).
const CREDENTIAL_FIELDS: Record<Gateway, ReadonlyArray<{ key: string; labelKey: string }>> = {
  paypal: [
    { key: 'client_id', labelKey: 'gateway.settings.credentials.fields.clientId' },
    { key: 'client_secret', labelKey: 'gateway.settings.credentials.fields.clientSecret' },
  ],
  stripe: [
    { key: 'publishable_key', labelKey: 'gateway.settings.credentials.fields.publishableKey' },
    { key: 'secret_key', labelKey: 'gateway.settings.credentials.fields.secretKey' },
    { key: 'webhook_secret', labelKey: 'gateway.settings.credentials.fields.webhookSecret' },
  ],
  payoneer: [
    { key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKey' },
    { key: 'partner_id', labelKey: 'gateway.settings.credentials.fields.partnerId' },
  ],
  lianlian: [
    { key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKey' },
    { key: 'merchant_id', labelKey: 'gateway.settings.credentials.fields.merchantId' },
  ],
  sunrate: [{ key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKeyOptional' }],
  pingpong: [{ key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKeyOptional' }],
  worldfirst: [{ key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKeyOptional' }],
  airwallex: [
    { key: 'api_key', labelKey: 'gateway.settings.credentials.fields.apiKey' },
    { key: 'client_id', labelKey: 'gateway.settings.credentials.fields.clientId' },
  ],
  bank: [],
};

interface GatewaySettings {
  gateway: string;
  enabled: boolean;
  tier_mode: 'instant' | 'manual';
  capabilities: {
    topup_push: boolean;
    topup_manual: boolean;
    fx_support: string[];
    webhook_verify: 'hmac' | 'signature' | 'ip_allowlist' | 'none';
  };
  credentials_present: boolean;
  health_status?: 'ok' | 'degraded' | 'down';
  health_latency_ms?: number;
  webhook_url?: string;
  imap_pattern?: {
    sender_regex: string;
    txn_id_regex: string;
    amount_regex: string;
  };
  recent_transactions?: Array<{
    txn_id: string;
    amount_usd_cents: number;
    status: string;
    created_at: string;
  }>;
}

// ---------------------------------------------------------------------------
// Surface tabs — Phase 3.2 (legacy 9-plugin shell) vs Phase 8.2 (new
// payment-gateways platform surface). Default lands on the Phase 8.2
// surface per PLAN/task-08 because that's the active spec; admins may
// still flip back to the legacy 9-plugin shell while the BE bridge is
// in transit.
// ---------------------------------------------------------------------------

type SurfaceTab = 'p8_payment_gateways' | 'p3_plugins';

export function GatewaySettingsPage(): JSX.Element {
  const { t } = useTranslation('finance');
  const [surface, setSurface] = useState<SurfaceTab>('p8_payment_gateways');

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div
        role="tablist"
        aria-label={t('gateway.surfaces.ariaLabel')}
        className="flex flex-wrap gap-2 rounded-lg border border-pw-border bg-pw-bg p-2"
      >
        <button
          type="button"
          role="tab"
          aria-selected={surface === 'p8_payment_gateways'}
          onClick={() => setSurface('p8_payment_gateways')}
          className={cn(
            'rounded-md px-4 py-2 text-sm font-medium transition-colors',
            surface === 'p8_payment_gateways'
              ? 'bg-pw-primary text-white'
              : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
          )}
          data-testid="gateway-surface-tab-p8"
        >
          {t('gateway.surfaces.tabP8')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={surface === 'p3_plugins'}
          onClick={() => setSurface('p3_plugins')}
          className={cn(
            'rounded-md px-4 py-2 text-sm font-medium transition-colors',
            surface === 'p3_plugins'
              ? 'bg-pw-primary text-white'
              : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
          )}
          data-testid="gateway-surface-tab-p3"
        >
          {t('gateway.surfaces.tabP3')}
        </button>
      </div>

      {surface === 'p8_payment_gateways' ? (
        <PaymentGatewaysPanel />
      ) : (
        <LegacyPluginGatewayPanel />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 3.2 — Legacy 9-plugin shell (unchanged behaviour, just renamed
// from the original GatewaySettingsPage so the new SurfaceTab wrapper
// can host it as a tab body).
// ---------------------------------------------------------------------------

function LegacyPluginGatewayPanel(): JSX.Element {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { t } = useTranslation('finance');

  const [active, setActive] = useState<Gateway>('paypal');
  const [credentialDraft, setCredentialDraft] = useState<Record<string, string>>({});
  const [tierDraft, setTierDraft] = useState<'instant' | 'manual' | null>(null);
  // `enabledDraft` was added so the toggle no longer auto-mutates on each
  // click — Gemini G-Bug-36 reproduced as "Save never enables on the
  // `bank` gateway" because `bank` has zero credential fields, tier is
  // locked, and the enabled toggle was the only mutation path but it
  // bypassed the draft → dirty stayed false forever. Now Save is the
  // single commit point for all three (enabled / tier / credentials).
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['admin', 'gateway-settings', active],
    queryFn: async (): Promise<GatewaySettings> => {
      const c = openapiApiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET(
        `/api/v1/admin/gateway-settings/${encodeURIComponent(active)}`,
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<GatewaySettings>(data);
    },
  });

  // Reset drafts whenever active gateway changes / fresh data lands.
  useEffect(() => {
    setCredentialDraft({});
    setTierDraft(null);
    setEnabledDraft(null);
  }, [active, settingsQuery.data?.gateway]);

  const saveMutation = useMutation({
    mutationFn: async (body: {
      enabled?: boolean;
      tier_mode?: 'instant' | 'manual';
      credentials?: Record<string, string>;
    }): Promise<GatewaySettings> => {
      const c = openapiApiClient() as unknown as LooseClient;
      const { data, error, response } = await c.PUT(
        `/api/v1/admin/gateway-settings/${encodeURIComponent(active)}`,
        {
          headers: { 'Idempotency-Key': newIdempotencyKey() },
          body,
        },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<GatewaySettings>(data);
    },
    onSuccess: (next) => {
      qc.setQueryData(['admin', 'gateway-settings', active], next);
      setCredentialDraft({});
      setTierDraft(null);
      setEnabledDraft(null);
      toast({ variant: 'success', title: t('gateway.settings.toast.savedTitle') });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('gateway.settings.toast.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('gateway.settings.networkError'),
      });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (): Promise<{ ok: boolean; latency_ms?: number; message?: string }> => {
      const c = openapiApiClient() as unknown as LooseClient;
      const { data, error, response } = await c.POST(
        `/api/v1/admin/gateway-settings/${encodeURIComponent(active)}/test-connection`,
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<{ ok: boolean; latency_ms?: number; message?: string }>(data);
    },
    onSuccess: (res) => {
      if (res.ok) {
        toast({
          variant: 'success',
          title: t('gateway.settings.test.healthyTitle'),
          description: res.latency_ms ? `${res.latency_ms} ms` : undefined,
        });
      } else {
        toast({
          variant: 'error',
          title: t('gateway.settings.test.failedTitle'),
          description: res.message ?? t('gateway.settings.test.nonOk'),
        });
      }
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('gateway.settings.test.connFailedTitle'),
        description: err instanceof Error ? err.message : t('gateway.settings.networkError'),
      });
    },
  });

  const regenMutation = useMutation({
    mutationFn: async (): Promise<{ webhook_url: string }> => {
      const c = openapiApiClient() as unknown as LooseClient;
      const { data, error, response } = await c.POST(
        `/api/v1/admin/gateway-settings/${encodeURIComponent(active)}/regenerate-webhook`,
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<{ webhook_url: string }>(data);
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('gateway.settings.toast.webhookRegeneratedTitle'),
        description: t('gateway.settings.toast.webhookRegeneratedDesc'),
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'gateway-settings', active] });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('gateway.settings.toast.webhookRegenerateFailedTitle'),
        description: err instanceof Error ? err.message : t('gateway.settings.networkError'),
      });
    },
  });

  const settings = settingsQuery.data;
  const isTier1 = useMemo(() => TIER1_GATEWAYS.includes(active as never), [active]);
  const isTier2 = !isTier1;
  const tierLocked = !HOT_SWAP_GATEWAYS.has(active);
  const effectiveTier = tierDraft ?? settings?.tier_mode ?? (isTier1 ? 'instant' : 'manual');

  const effectiveEnabled =
    enabledDraft !== null ? enabledDraft : settings?.enabled ?? false;

  const dirty =
    Object.keys(credentialDraft).length > 0 ||
    (tierDraft !== null && tierDraft !== settings?.tier_mode) ||
    (enabledDraft !== null && enabledDraft !== settings?.enabled);

  function healthBadge(): JSX.Element | null {
    if (!settings) return null;
    // Bug 18 fix: BE handler `serializeGatewaySettings` does NOT populate
    // health_status (no live ping cron yet). Previously the FE defaulted
    // `?? 'ok'` so the badge ALWAYS showed "Healthy" regardless of actual
    // gateway state. Now: undefined → "Untested" gray badge. A real value
    // arrives only when BE wires a health probe; "Test connection" button
    // results land in a toast (testMutation onSuccess) but don't persist
    // back to settings yet.
    const status = settings.health_status;
    if (!status) {
      return (
        <span
          className="inline-flex items-center gap-2 rounded-full bg-pw-surface px-3 py-1 text-xs font-medium text-pw-text-muted"
          title={t('gateway.settings.health.untestedTitle')}
        >
          <span className="h-2 w-2 rounded-full bg-pw-text-muted/50" />
          {t('gateway.settings.health.untested')}
        </span>
      );
    }
    const tone =
      status === 'ok'
        ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
        : status === 'degraded'
          ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
          : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300';
    const dot =
      status === 'ok' ? 'bg-emerald-500' : status === 'degraded' ? 'bg-amber-500' : 'bg-red-500';
    const label =
      status === 'ok'
        ? `${t('gateway.settings.health.healthy')}${settings.health_latency_ms ? ` · ${settings.health_latency_ms}ms` : ''}`
        : status === 'degraded'
          ? t('gateway.settings.health.degraded')
          : t('gateway.settings.health.down');
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium',
          tone,
        )}
      >
        <span className={cn('h-2 w-2 rounded-full', dot)} />
        {label}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight text-pw-text">
          {t('gateway.settings.legacy.heading')}
        </h1>
        <p className="mt-1 text-sm text-pw-text-muted">
          {t('gateway.settings.legacy.subtitle')}
        </p>
      </header>

      {/* ---- Tab pill row ---- */}
      <div className="flex flex-wrap gap-2 rounded-lg border border-pw-border bg-pw-bg p-2">
        {ALL_GATEWAYS.map((gw) => (
          <button
            key={gw}
            type="button"
            onClick={() => setActive(gw)}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              active === gw
                ? 'bg-pw-primary text-white'
                : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
            )}
            data-testid={`gateway-tab-${gw}`}
          >
            {GATEWAY_LABEL[gw]}
          </button>
        ))}
      </div>

      {/* ---- Active gateway card ---- */}
      <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
        {settingsQuery.isLoading ? (
          <p className="text-sm text-pw-text-muted">{t('gateway.settings.loading', { gateway: GATEWAY_LABEL[active] })}</p>
        ) : settingsQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-red-300">
            {t('gateway.settings.loadFailedPrefix')}{' '}
            {settingsQuery.error instanceof Error ? settingsQuery.error.message : 'unknown'}
          </p>
        ) : settings ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-pw-text">
                  {GATEWAY_LABEL[active]}
                </h2>
                {healthBadge()}
              </div>
              <label className="flex items-center gap-3">
                <span className="text-sm font-medium text-pw-text">{t('gateway.settings.fields.enabled')}</span>
                <input
                  type="checkbox"
                  className="h-5 w-10 cursor-pointer accent-pw-primary-strong"
                  checked={effectiveEnabled}
                  onChange={(e) => setEnabledDraft(e.target.checked)}
                  data-testid={`gateway-enabled-${active}`}
                />
              </label>
            </div>

            {/* ---- Tier mode ---- */}
            <div className="mt-6">
              <label
                htmlFor="tier-mode"
                className="block text-sm font-semibold text-pw-text"
              >
                {t('gateway.settings.fields.tierMode')}
              </label>
              <Select
                value={effectiveTier}
                disabled={tierLocked}
                onValueChange={(value) =>
                  setTierDraft(value === 'instant' ? 'instant' : 'manual')
                }
              >
                <SelectTrigger
                  id="tier-mode"
                  className="mt-2"
                  data-testid={`gateway-tier-${active}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">
                    {t('gateway.settings.tierOption.instant', {
                      lockSuffix: tierLocked && isTier1 ? t('gateway.settings.tierOption.lockedSuffix') : '',
                    })}
                  </SelectItem>
                  <SelectItem value="manual">
                    {t('gateway.settings.tierOption.manual', {
                      lockSuffix: tierLocked && isTier2 ? t('gateway.settings.tierOption.lockedSuffix') : '',
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
              {tierLocked ? (
                <p className="mt-1 text-xs text-pw-text-muted">
                  {isTier1
                    ? t('gateway.settings.tierLockedTier1')
                    : t('gateway.settings.tierLockedTier2')}
                </p>
              ) : null}
            </div>

            {/* ---- Credentials ---- */}
            {CREDENTIAL_FIELDS[active].length > 0 ? (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.credentials.heading')}</h3>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {CREDENTIAL_FIELDS[active].map((f) => (
                    <div key={f.key} className="flex flex-col gap-2">
                      <label
                        htmlFor={`cred-${f.key}`}
                        className="text-sm font-medium text-pw-text"
                      >
                        {t(f.labelKey)}
                      </label>
                      <Input
                        id={`cred-${f.key}`}
                        type={f.key.includes('secret') || f.key.includes('password') ? 'password' : 'text'}
                        value={credentialDraft[f.key] ?? ''}
                        placeholder={
                          settings.credentials_present
                            ? 'AYxx••••••••••••'
                            : t('gateway.settings.credentials.placeholder')
                        }
                        onChange={(e) =>
                          setCredentialDraft((prev) => ({ ...prev, [f.key]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* ---- Webhook URL (Tier 1) ---- */}
            {isTier1 && settings.webhook_url ? (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.webhook.heading')}</h3>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <Input value={settings.webhook_url} readOnly className="flex-1 font-mono text-xs" />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard?.writeText(settings.webhook_url ?? '');
                      toast({ variant: 'success', title: t('gateway.settings.toast.webhookCopiedTitle') });
                    }}
                  >
                    {t('gateway.settings.webhook.copy')}
                  </Button>
                  <Button variant="destructive" onClick={() => setConfirmRegen(true)}>
                    {t('gateway.settings.webhook.regenerate')}
                  </Button>
                </div>
              </div>
            ) : null}

            {/* ---- IMAP pattern preview (Tier 2) ---- */}
            {isTier2 && settings.imap_pattern ? (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.imapPreview.heading')}</h3>
                <div className="mt-2 rounded-lg bg-pw-surface p-3 font-mono text-xs text-pw-text-muted">
                  <p>{t('gateway.settings.imapPreview.sender')}: {settings.imap_pattern.sender_regex}</p>
                  <p>{t('gateway.settings.imapPreview.txnId')}: {settings.imap_pattern.txn_id_regex}</p>
                  <p>{t('gateway.settings.imapPreview.amount')}: {settings.imap_pattern.amount_regex}</p>
                </div>
                <a
                  href="/billing/imap-config"
                  className="mt-2 inline-block text-xs font-medium text-pw-primary hover:underline"
                >
                  {t('gateway.settings.imapPreview.editLink')}
                </a>
              </div>
            ) : null}

            {/* ---- Recent transactions ---- */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.recentTx.heading')}</h3>
              {!settings.recent_transactions || settings.recent_transactions.length === 0 ? (
                <p className="mt-2 text-xs text-pw-text-muted">{t('gateway.settings.recentTx.empty')}</p>
              ) : (
                <div className="overflow-x-auto">
                <table className="mt-2 w-full text-xs">
                  <thead>
                    <tr className="border-b border-pw-border text-left text-pw-text-muted">
                      <th className="py-2 font-medium">{t('gateway.settings.recentTx.colTxId')}</th>
                      <th className="py-2 font-medium">{t('gateway.settings.recentTx.colAmount')}</th>
                      <th className="py-2 font-medium">{t('gateway.settings.recentTx.colStatus')}</th>
                      <th className="py-2 font-medium">{t('gateway.settings.recentTx.colDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.recent_transactions.slice(0, 10).map((row) => (
                      <tr key={row.txn_id} className="border-b border-pw-border last:border-0">
                        <td className="py-2 font-mono">{row.txn_id}</td>
                        <td className="py-2">${(row.amount_usd_cents / 100).toFixed(2)}</td>
                        <td className="py-2">{row.status}</td>
                        <td className="py-2 text-pw-text-muted">
                          {new Date(row.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>

            {/* ---- Footer actions ---- */}
            <div className="mt-6 flex items-center justify-end gap-3">
              {isTier1 ? (
                <Button
                  variant="secondary"
                  disabled={testMutation.isPending}
                  onClick={() => testMutation.mutate()}
                  data-testid={`gateway-test-${active}`}
                >
                  {testMutation.isPending ? t('gateway.settings.test.testing') : t('gateway.settings.test.testConnection')}
                </Button>
              ) : null}
              <Button
                disabled={!dirty || saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    ...(enabledDraft !== null && enabledDraft !== settings.enabled
                      ? { enabled: enabledDraft }
                      : {}),
                    ...(tierDraft && tierDraft !== settings.tier_mode ? { tier_mode: tierDraft } : {}),
                    ...(Object.keys(credentialDraft).length > 0
                      ? { credentials: credentialDraft }
                      : {}),
                  })
                }
                data-testid={`gateway-save-${active}`}
              >
                {saveMutation.isPending ? t('gateway.settings.savingShort') : t('gateway.settings.save')}
              </Button>
            </div>
          </>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirmRegen}
        onOpenChange={setConfirmRegen}
        title={t('gateway.settings.regenConfirm.title')}
        description={t('gateway.settings.regenConfirm.desc')}
        confirmLabel={t('gateway.settings.regenConfirm.confirm')}
        type="destructive"
        onConfirm={() => regenMutation.mutate()}
      />
    </div>
  );
}

// ===========================================================================
// Phase 8.2 Task 08 — Payment Gateways platform-global panel
// ===========================================================================
//
// Distinct from the Phase 3.2 9-plugin shell above (different endpoint
// surface, different domain shape). This panel implements the contract
// at .vg/phases/08.2-admin-finance-catalog/API-CONTRACTS.md §664-985:
//
//   GET   /api/v1/admin/payment-gateways
//   PATCH /api/v1/admin/payment-gateways/:id
//   POST  /api/v1/admin/payment-gateways/:id/reveal
//   POST  /api/v1/admin/payment-gateways/:id/pause
//   POST  /api/v1/admin/payment-gateways/:id/resume
//
// Features:
//   - Always-masked credentials (`****XXXX`) on page load.
//   - Reveal modal — password confirm + 30s auto re-mask + audit log
//     (GatewayRevealModal — features/billing/).
//   - Fee config — flat / percent / combined via
//     GatewayFeeForm (features/billing/), vendor bears fee.
//   - Pause / Resume buttons with confirm dialog + reason capture
//     . Optimistic invalidation on success.
//   - Health badge — refetched every 60s (poll). No auto-switch on
//     failure — queue pause UI banner only.
//   - WebSocket subscribe for `payment-gateways.*` channel so concurrent
//     admin edits invalidate the local list without waiting for the 60s
//     poll. Fail-safe: socket may be unavailable in dev/tests — the
//     polling refetch still keeps state coherent.

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS.md zod schemas Block 2
// ---------------------------------------------------------------------------

interface PaymentGatewayRow {
  id: string;
  code: string;
  name: string;
  status: 'enabled' | 'disabled' | 'paused';
  masked_key: string;
  fee: GatewayFeeConfig;
  auto_approve: boolean;
  last_health_check_at: string | null;
  health: 'healthy' | 'degraded' | 'down';
}

interface PauseGatewayResponse {
  id: string;
  status: 'paused';
  paused_at: string;
  audit_log_id: string;
}

interface ResumeGatewayResponse {
  id: string;
  status: 'enabled';
  resumed_at: string;
  audit_log_id: string;
}

const PAYMENT_GATEWAYS_QK = ['admin', 'payment-gateways'] as const;

// ---------------------------------------------------------------------------
// Idempotency-Key helper (per F-04)
// ---------------------------------------------------------------------------

function newP8IdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `p8gw-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Helpers — health badge tone, masked render
// ---------------------------------------------------------------------------

function P8HealthBadge({
  health,
  status,
  lastCheckAt,
}: {
  health: PaymentGatewayRow['health'];
  status: PaymentGatewayRow['status'];
  lastCheckAt: string | null;
}): JSX.Element {
  const { t } = useTranslation('finance');
  // If gateway is paused, the health badge is overridden with a
  // "Maintenance" tone so the operator sees the deliberate pause state
  // first — matches the merchant-portal Maintenance badge.
  if (status === 'paused') {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full bg-amber-100 dark:bg-amber-950/40 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300"
        data-testid="gateway-status-badge-paused"
        title={lastCheckAt ? t('gateway.settings.p8health.lastCheck', { at: lastCheckAt }) : undefined}
      >
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        {t('gateway.settings.p8health.maintenance')}
      </span>
    );
  }
  const tone =
    health === 'healthy'
      ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
      : health === 'degraded'
        ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
        : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300';
  const dot =
    health === 'healthy'
      ? 'bg-emerald-500'
      : health === 'degraded'
        ? 'bg-amber-500'
        : 'bg-red-500';
  const label =
    health === 'healthy'
      ? t('gateway.settings.p8health.healthy')
      : health === 'degraded'
        ? t('gateway.settings.p8health.degraded')
        : t('gateway.settings.p8health.down');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium',
        tone,
      )}
      data-testid={`gateway-status-badge-${health}`}
      title={lastCheckAt ? t('gateway.settings.p8health.lastCheck', { at: lastCheckAt }) : t('gateway.settings.p8health.noProbe')}
    >
      <span className={cn('h-2 w-2 rounded-full', dot)} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pause/Resume confirm dialog — captures a reason so the audit log
// payload satisfies BE zod min(10) requirement.
// ---------------------------------------------------------------------------

function PauseResumeDialog({
  open,
  onOpenChange,
  gatewayId,
  gatewayName,
  action,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gatewayId: string;
  gatewayName: string;
  action: 'pause' | 'resume';
  onConfirm: (reasonOrNotes: string) => void;
  isPending: boolean;
}): JSX.Element {
  const { t } = useTranslation('finance');
  const [reason, setReason] = useState('');
  const min = action === 'pause' ? 10 : 0;
  const canSubmit = reason.trim().length >= min;

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  const title =
    action === 'pause'
      ? t('gateway.settings.pauseDialog.titlePause', { gateway: gatewayName })
      : t('gateway.settings.pauseDialog.titleResume', { gateway: gatewayName });
  const description =
    action === 'pause'
      ? t('gateway.settings.pauseDialog.descPause', { min })
      : t('gateway.settings.pauseDialog.descResume');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      aria-label={title}
    >
      <div
        data-testid={`gateway-${action}-dialog-${gatewayId}`}
        className="flex flex-col gap-4"
      >
        <div>
          <h3 className="text-lg font-semibold text-pw-text">{title}</h3>
          <p className="mt-1 text-sm text-pw-text-muted">{description}</p>
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor={`gateway-${action}-reason-${gatewayId}`}
            className="text-xs font-medium text-pw-text"
          >
            {action === 'pause'
              ? t('gateway.settings.pauseDialog.reasonLabelPause', { min })
              : t('gateway.settings.pauseDialog.reasonLabelResume')}
          </label>
          <Input
            id={`gateway-${action}-reason-${gatewayId}`}
            data-testid={`gateway-${action}-reason-input`}
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isPending}
            placeholder={
              action === 'pause'
                ? t('gateway.settings.pauseDialog.reasonPlaceholderPause')
                : t('gateway.settings.pauseDialog.reasonPlaceholderResume')
            }
          />
          {action === 'pause' && reason.length > 0 && reason.length < min ? (
            <p
              role="alert"
              className="text-xs text-red-600 dark:text-red-300"
              data-testid={`gateway-${action}-reason-error`}
            >
              {t('gateway.settings.pauseDialog.reasonTooShort', { min, count: reason.length })}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('gateway.settings.pauseDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant={action === 'pause' ? 'destructive' : 'default'}
            disabled={!canSubmit || isPending}
            onClick={() => {
              if (canSubmit) onConfirm(reason.trim());
            }}
            data-testid={`gateway-${action}-confirm-btn`}
          >
            {isPending
              ? t('gateway.settings.pauseDialog.processing')
              : action === 'pause'
                ? t('gateway.settings.pauseDialog.confirmPause')
                : t('gateway.settings.pauseDialog.confirmResume')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Single-row card — masked credentials + actions + fee form
// ---------------------------------------------------------------------------

function PaymentGatewayCard({ row }: { row: PaymentGatewayRow }): JSX.Element {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { t } = useTranslation('finance');
  const [revealOpen, setRevealOpen] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);

  const pauseMutation = useMutation<PauseGatewayResponse, Error, { reason: string }>({
    mutationFn: (body) =>
      p8ApiClient.post<PauseGatewayResponse>(
        `/api/v1/admin/payment-gateways/${encodeURIComponent(row.id)}/pause`,
        body,
        { idempotencyKey: newP8IdempotencyKey() },
      ),
    onSuccess: (res) => {
      toast({
        variant: 'success',
        title: t('gateway.settings.toastP8.pausedTitle', { gateway: row.name }),
        description: `Audit log: ${res.audit_log_id}`,
      });
      setPauseDialogOpen(false);
      void qc.invalidateQueries({ queryKey: PAYMENT_GATEWAYS_QK });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : err.message || t('gateway.settings.toastP8.networkError');
      const status = err instanceof ApiError ? err.status : 0;
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      toast({
        variant: 'error',
        title:
          code === 'ALREADY_PAUSED' || status === 409
            ? t('gateway.settings.toastP8.alreadyPausedTitle')
            : t('gateway.settings.toastP8.pauseFailedTitle'),
        description: msg,
      });
    },
  });

  const resumeMutation = useMutation<ResumeGatewayResponse, Error, { notes?: string }>({
    mutationFn: (body) =>
      p8ApiClient.post<ResumeGatewayResponse>(
        `/api/v1/admin/payment-gateways/${encodeURIComponent(row.id)}/resume`,
        body,
        { idempotencyKey: newP8IdempotencyKey() },
      ),
    onSuccess: (res) => {
      toast({
        variant: 'success',
        title: t('gateway.settings.toastP8.resumedTitle', { gateway: row.name }),
        description: `Audit log: ${res.audit_log_id}`,
      });
      setResumeDialogOpen(false);
      void qc.invalidateQueries({ queryKey: PAYMENT_GATEWAYS_QK });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : err.message || t('gateway.settings.toastP8.networkError');
      const status = err instanceof ApiError ? err.status : 0;
      const code = err instanceof ApiError ? err.code : 'UNKNOWN';
      toast({
        variant: 'error',
        title:
          code === 'NOT_PAUSED' || status === 409
            ? t('gateway.settings.toastP8.notPausedTitle')
            : t('gateway.settings.toastP8.resumeFailedTitle'),
        description: msg,
      });
    },
  });

  const isPaused = row.status === 'paused';

  return (
    <article
      className="rounded-xl border border-pw-border bg-pw-bg p-6"
      data-testid={`payment-gateway-card-${row.id}`}
    >
      {isPaused ? (
        <div
          className="mb-4 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-xs text-amber-800 dark:text-amber-300"
          data-testid={`gateway-pause-banner-${row.id}`}
        >
          {t('gateway.settings.pauseBanner')}
        </div>
      ) : null}

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-pw-text">{row.name}</h2>
          <span className="rounded-md bg-pw-surface px-2 py-0.5 text-xs font-mono text-pw-text-muted">
            {row.code}
          </span>
          <P8HealthBadge
            health={row.health}
            status={row.status}
            lastCheckAt={row.last_health_check_at}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isPaused ? (
            <Button
              variant="destructive"
              onClick={() => setPauseDialogOpen(true)}
              disabled={pauseMutation.isPending}
              data-testid={`gateway-pause-btn-${row.id}`}
            >
              {t('gateway.settings.card.pause')}
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={() => setResumeDialogOpen(true)}
              disabled={resumeMutation.isPending}
              data-testid={`gateway-resume-btn-${row.id}`}
            >
              {t('gateway.settings.card.resume')}
            </Button>
          )}
        </div>
      </header>

      <section className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.card.apiKey')}</h3>
          <div className="mt-2 flex items-center gap-2">
            <code
              className="flex-1 rounded-md bg-pw-surface px-3 py-2 font-mono text-sm text-pw-text"
              data-testid={`gateway-masked-key-${row.id}`}
            >
              {row.masked_key}
            </code>
            <Button
              variant="secondary"
              onClick={() => setRevealOpen(true)}
              data-testid={`gateway-reveal-btn-${row.id}`}
            >
              {t('gateway.settings.card.reveal')}
            </Button>
          </div>
          <p className="mt-2 text-xs text-pw-text-muted">
            {t('gateway.settings.card.auditNotice')}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-pw-text">{t('gateway.settings.card.statusHeading')}</h3>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-pw-text-muted">{t('gateway.settings.card.statusLabel')}</dt>
              <dd
                className="font-medium text-pw-text"
                data-testid={`gateway-status-${row.id}`}
              >
                {t(`gateway.settings.panel.status${row.status.charAt(0).toUpperCase()}${row.status.slice(1)}`)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-pw-text-muted">{t('gateway.settings.card.autoApprove')}</dt>
              <dd className="font-medium text-pw-text">
                {row.auto_approve ? t('gateway.settings.card.yes') : t('gateway.settings.card.no')}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-pw-text-muted">{t('gateway.settings.card.lastHealthCheck')}</dt>
              <dd className="font-mono text-xs text-pw-text-muted">
                {row.last_health_check_at
                  ? new Date(row.last_health_check_at).toLocaleString()
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="mt-5">
        <GatewayFeeForm
          gatewayId={row.id}
          gatewayName={row.name}
          initialFee={row.fee}
        />
      </section>

      <GatewayRevealModal
        open={revealOpen}
        onOpenChange={setRevealOpen}
        gatewayId={row.id}
        gatewayName={row.name}
      />

      <PauseResumeDialog
        open={pauseDialogOpen}
        onOpenChange={setPauseDialogOpen}
        gatewayId={row.id}
        gatewayName={row.name}
        action="pause"
        isPending={pauseMutation.isPending}
        onConfirm={(reason) => pauseMutation.mutate({ reason })}
      />
      <PauseResumeDialog
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        gatewayId={row.id}
        gatewayName={row.name}
        action="resume"
        isPending={resumeMutation.isPending}
        onConfirm={(notes) =>
          resumeMutation.mutate(notes ? { notes } : {})
        }
      />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Top-level Phase 8.2 panel — list + status filter + 60s health poll
// ---------------------------------------------------------------------------

function PaymentGatewaysPanel(): JSX.Element {
  const { t } = useTranslation('finance');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'enabled' | 'disabled' | 'paused'
  >('all');

  // Phase 8.2 audit 2026-05-23 — URL-synced page + pageSize per /stores
  // standard. BE returns the full gateway list as an array (no total / page
  // meta), so paging is applied client-side to keep the Rows-per-page
  // selector reachable without re-shaping the contract.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
  );
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? '25',
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;

  const setPage = (nextPage: number): void => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };
  const handlePageSizeChange = (size: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(size));
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const listQuery = useQuery<PaymentGatewayRow[]>({
    queryKey: [...PAYMENT_GATEWAYS_QK, statusFilter],
    queryFn: async () => {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      return p8ApiClient.get<PaymentGatewayRow[]>(
        `/api/v1/admin/payment-gateways${qs}`,
      );
    },
    // 60s health-check poll — pulls fresh `health` + `last_health_check_at`
    // for every gateway without forcing the admin to refresh manually.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const allRows = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visibleRows = useMemo(
    () => allRows.slice((page - 1) * pageSize, page * pageSize),
    [allRows, page, pageSize],
  );

  return (
    <div className="flex flex-col gap-6" data-testid="payment-gateways-panel">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-pw-text">
            {t('gateway.settings.panel.heading')}
          </h1>
          <p className="mt-1 text-sm text-pw-text-muted">
            {t('gateway.settings.panel.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="payment-gateways-status-filter"
            className="text-sm font-medium text-pw-text-muted"
          >
            {t('gateway.settings.panel.statusFilterLabel')}
          </label>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(
                value as 'all' | 'enabled' | 'disabled' | 'paused',
              );
              setPage(1);
            }}
          >
            <SelectTrigger
              id="payment-gateways-status-filter"
              data-testid="payment-gateways-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('gateway.settings.panel.statusFilterAll')}</SelectItem>
              <SelectItem value="enabled">{t('gateway.settings.panel.statusEnabled')}</SelectItem>
              <SelectItem value="paused">{t('gateway.settings.panel.statusPaused')}</SelectItem>
              <SelectItem value="disabled">{t('gateway.settings.panel.statusDisabled')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {listQuery.isLoading ? (
        <p className="text-sm text-pw-text-muted">{t('gateway.settings.panel.loadingList')}</p>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 dark:text-red-300">
          {t('gateway.settings.panel.loadFailedPrefix')}{' '}
          {listQuery.error instanceof Error
            ? listQuery.error.message
            : t('gateway.settings.panel.loadFailedUnknown')}
        </p>
      ) : total === 0 ? (
        <p
          className="text-sm text-pw-text-muted"
          data-testid="payment-gateways-empty"
        >
          {t('gateway.settings.panel.empty')}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {visibleRows.map((row) => (
              <PaymentGatewayCard key={row.id} row={row} />
            ))}
          </div>
          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. Client-side paging
              because the BE list endpoint returns the full set as an array. */}
          <PageListFooter
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            loading={listQuery.isFetching}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            testId="payment-gateways-footer"
          />
        </>
      )}
    </div>
  );
}
